import { useMutation, useApolloClient } from '@apollo/client'
import { toast } from '@buffer-mono/popcorn'
import {
  ADD_NEW_CUSTOM_LINK,
  DELETE_CUSTOM_LINK,
  SWAP_CUSTOM_LINKS,
  UPDATE_CUSTOM_LINK,
  UPDATE_CUSTOM_LINK_COLORS,
  UPDATE_POST_LINK,
} from './queries'
import { GET_CUSTOM_LINKS } from '~publish/pages/Channel/ChannelShopGrid/ChannelShopGrid'
import type {
  ShopGridCustomLinksPayload,
  UpdateShopGridCustomLinkInput,
  UpdateShopGridPostLinkInput,
  UpdateShopGridPostLinkPayload,
  SwapShopGridCustomLinksInput,
  AddShopGridCustomLinkInput,
  DeleteShopGridCustomLinkInput,
} from '~publish/gql/graphql'
import { BufferTrackerReact } from '@buffer-mono/tracking-plan'
import { getChannelProperties } from '../util/index'
import type { Profile } from '~publish/legacy/profile-sidebar/types'
import type { UpdateCustomLinksColorsInput } from '@buffer-mono/app-shell/src/gql/graphql'

function updateApolloCache({
  apollo,
  channelId,
  data,
}: {
  apollo: ReturnType<typeof useApolloClient>
  channelId: string
  data: {
    links?: ShopGridCustomLinksPayload['links']
    color?: string
    contrastColor?: string
  }
}): void {
  const currentData = apollo.readQuery({
    query: GET_CUSTOM_LINKS,
    variables: {
      input: {
        channelId,
      },
    },
  })

  apollo.writeQuery({
    query: GET_CUSTOM_LINKS,
    data: {
      shopGridCustomLinks: {
        ...currentData?.shopGridCustomLinks,
        ...data,
      },
    },
    variables: {
      input: {
        channelId,
      },
    },
  })
}

export function useDeleteCustomLink({
  channelId,
}: {
  channelId: string
}): ReturnType<
  typeof useMutation<
    { deleteShopGridCustomLink: ShopGridCustomLinksPayload },
    { input: DeleteShopGridCustomLinkInput }
  >
> {
  const apollo = useApolloClient()

  return useMutation<
    { deleteShopGridCustomLink: ShopGridCustomLinksPayload },
    { input: DeleteShopGridCustomLinkInput }
  >(DELETE_CUSTOM_LINK, {
    onCompleted: (data) => {
      toast.success('Nice! Your changes have been saved.')

      updateApolloCache({
        apollo,
        channelId,
        data: {
          links: data.deleteShopGridCustomLink.links,
        },
      })
    },
    onError: () => {
      toast.error('There was an error saving your changes!')
    },
  })
}

export function useAddNewCustomLink({
  channelId,
}: {
  channelId: string
}): ReturnType<
  typeof useMutation<
    { addShopGridCustomLink: ShopGridCustomLinksPayload },
    { input: AddShopGridCustomLinkInput }
  >
> {
  const apollo = useApolloClient()

  return useMutation<
    { addShopGridCustomLink: ShopGridCustomLinksPayload },
    { input: AddShopGridCustomLinkInput }
  >(ADD_NEW_CUSTOM_LINK, {
    onCompleted: (data) => {
      toast.success('Nice! Your changes have been saved.')

      updateApolloCache({
        apollo,
        channelId,
        data: {
          links: data.addShopGridCustomLink.links,
        },
      })
    },
    onError: () => {
      toast.error('There was an error saving your changes!')
    },
  })
}

export function useUpdateCustomLinkColors({
  channelId,
}: {
  channelId: string
}): ReturnType<
  typeof useMutation<
    { updateShopGridCustomLinkColors: ShopGridCustomLinksPayload },
    { input: UpdateCustomLinksColorsInput }
  >
> {
  return useMutation<
    { updateShopGridCustomLinkColors: ShopGridCustomLinksPayload },
    { input: UpdateCustomLinksColorsInput }
  >(UPDATE_CUSTOM_LINK_COLORS, {
    refetchQueries: [
      {
        query: GET_CUSTOM_LINKS,
        variables: {
          input: {
            channelId,
          },
        },
      },
    ],
  })
}

export function useUpdatePostLink({
  refetch,
  profile,
}: {
  refetch: () => void
  profile: Profile
}): ReturnType<
  typeof useMutation<
    { updateShopGridPostLink: UpdateShopGridPostLinkPayload },
    { input: UpdateShopGridPostLinkInput }
  >
> {
  return useMutation<
    { updateShopGridPostLink: UpdateShopGridPostLinkPayload },
    {
      input: UpdateShopGridPostLinkInput
    }
  >(UPDATE_POST_LINK, {
    onCompleted: (data, options = {}) => {
      toast.success('Nice! Your changes have been saved.')

      BufferTrackerReact.shopGridPostURLUpdated({
        ...getChannelProperties(profile),
        postId: options.variables?.input.postId,
        url: options.variables?.input.link,
      })

      refetch()
    },
    onError: () => {
      toast.error('There was an error saving your changes!')
    },
  })
}

export function useEditCustomLink({
  channelId,
}: {
  channelId: string
}): ReturnType<
  typeof useMutation<
    { updateShopGridCustomLink: ShopGridCustomLinksPayload },
    { input: UpdateShopGridCustomLinkInput }
  >
> {
  const apollo = useApolloClient()

  return useMutation<
    { updateShopGridCustomLink: ShopGridCustomLinksPayload },
    { input: UpdateShopGridCustomLinkInput }
  >(UPDATE_CUSTOM_LINK, {
    onCompleted: (data) => {
      toast.success('Nice! Your changes have been saved.')

      updateApolloCache({
        apollo,
        channelId,
        data: {
          color: data.updateShopGridCustomLink.color,
          contrastColor: data.updateShopGridCustomLink.contrastColor,
        },
      })
    },
    onError: () => {
      toast.error('There was an error saving your changes!')
    },
  })
}

export function useSwapCustomLinks({
  channelId,
}: {
  channelId: string
}): ReturnType<
  typeof useMutation<
    { swapShopGridCustomLinks: ShopGridCustomLinksPayload },
    { input: SwapShopGridCustomLinksInput }
  >
> {
  const apollo = useApolloClient()

  return useMutation<
    { swapShopGridCustomLinks: ShopGridCustomLinksPayload },
    { input: SwapShopGridCustomLinksInput }
  >(SWAP_CUSTOM_LINKS, {
    onCompleted: (data) => {
      toast.success('Nice! Your changes have been saved.')

      updateApolloCache({
        apollo,
        channelId,
        data: {
          links: data.swapShopGridCustomLinks.links,
        },
      })
    },
    onError: () => {
      toast.error('There was an error saving your changes!')
    },
  })
}
